"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Check } from "lucide-react";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-accent/30 bg-accent-light/10 p-8 shadow-soft backdrop-blur-sm">
        <div className="flex size-12 items-center justify-center rounded-full bg-accent">
          <Check className="size-8 text-white" />
        </div>
        <p className="text-center text-lg font-medium text-foreground-dark">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2 border-accent/40 bg-transparent text-foreground-dark hover:bg-accent/20 hover:text-accent">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark/90">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" {...field} className="border-muted/40 bg-dark-background/50 text-foreground-dark placeholder:text-muted-foreground/60 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="border-muted/40 bg-dark-background/50 text-foreground-dark placeholder:text-muted-foreground/60 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        minLength: {
          value: 10,
          message: "Phone number must be at least 10 digits"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark/90">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="border-muted/40 bg-dark-background/50 text-foreground-dark placeholder:text-muted-foreground/60 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "Subject is required",
        minLength: {
          value: 3,
          message: "Subject must be at least 3 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark/90">Subject</FormLabel>
              <FormControl>
                <Input placeholder="How can we help?" {...field} className="border-muted/40 bg-dark-background/50 text-foreground-dark placeholder:text-muted-foreground/60 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-dark/90">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us more about your needs..." {...field} className="min-h-32 resize-none border-muted/40 bg-dark-background/50 text-foreground-dark placeholder:text-muted-foreground/60 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/50" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 backdrop-blur-sm">
          <p className="text-sm text-red-400">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full scale-100 transform bg-accent px-8 py-3 text-base font-semibold text-white shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-[1.02] hover:bg-accent-hover hover:shadow-strong active:scale-100 active:bg-accent-active disabled:scale-100 disabled:opacity-50">
        {isSubmitting ? <>
            <span className="inline-block size-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            <span>Sending...</span>
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function ContactAndConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section ref={ref} className={`bg-dark-background px-4 py-16 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] md:py-20 lg:py-24 ${isVisible ? "animate-fade-in-up" : "translate-y-4 opacity-0"}`}>
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h2 className="mb-3 text-3xl font-bold text-foreground-dark md:text-4xl">
            Contact & Consultation
          </h2>
          <p className="text-base text-foreground-dark/70 md:text-lg">
            Get in touch with us. We'd love to hear from you and discuss how we can help.
          </p>
        </div>

        <FormProvider className="rounded-lg border border-muted/20 bg-dark-background/80 p-6 shadow-medium backdrop-blur-md md:p-8" formId="contact-consultation-form" sectionName="contact-consultation-section" defaultValues={{
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          minLength: 10
        },
        subject: {
          required: true,
          minLength: 3,
          maxLength: 150
        },
        message: {
          required: true,
          minLength: 10,
          maxLength: 1000
        }
      }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you within 24 hours.">
          <ContactForm />
        </FormProvider>
      </div>
    </section>;
}